/**
 * 3CX screen-pop panel (issue #61). Opened by the 3CX Windows client via
 * /calls/pop?number=%CallerNumber%.
 *
 * Full flow:
 * 1. Lookup caller by phone number (cached + live CW search)
 * 2. If ambiguous, show candidates with name + company → accept one
 * 3. After contact confirmed: "Create new ticket" or "Link existing ticket"
 * 4. On link/create: call details (start/end/duration/summary) go to CW ticket notes
 */
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Phone, Building2, User, Ticket as TicketIcon, Clock, AlertTriangle, ExternalLink, Search, Check, Plus, Link as LinkIcon, Loader2 } from 'lucide-react';
import { useGet, apiPost } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';

interface PopTicket {
  id: string; cwTicketId: number; summary: string | null; status: string | null;
  priority: string | null; boardName: string | null; cwUpdatedAt: string | null;
}
interface PopRecentCall {
  id: string; startedAt: string; direction: string; durationSec: number | null;
  status: string; cwTicketId: number | null;
}
interface PopLookup {
  query: { number: string; normalized: string };
  match: {
    outcome: 'contact' | 'company' | 'ambiguous' | 'none';
    contactId: number | null; companyId: number | null; reason: string;
    strong: boolean; ticketId: string | null; ticketCwId: number | null;
  };
  candidates: Array<{
    contactId: number | null; companyId: number | null; matchedOn?: string;
    name?: string | null; companyName?: string | null;
  }>;
  caller: { contactName: string | null; companyName: string | null; email: string | null } | null;
  company: { cwCompanyId: number; identifier: string; name: string } | null;
  openTickets: PopTicket[];
  recentCalls: PopRecentCall[];
}

export function CallsPopPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const number = params.get('number')?.trim() ?? '';
  const { data, loading, error, refetch } = useGet<PopLookup>(
    number ? '/api/v1/calls/lookup?number=' + encodeURIComponent(number) : null,
  );

  const title = 'Incoming call';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:px-6" role="region" aria-label={title}>
      <header className="flex items-center gap-3 mb-6">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-600 text-white shadow-md" aria-hidden="true">
          <Phone className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-[hsl(var(--content))]">{title}</h1>
          <p className="text-sm text-[hsl(var(--content-subtle))] truncate" data-testid="pop-number">
            {number || 'No number provided'}
          </p>
        </div>
      </header>

      {!number && (
        <EmptyState
          icon="📞"
          title="No caller number"
          description="Open this page from the 3CX client with ?number=%CallerNumber%, or add a number manually below."
          action={
            <form className="flex gap-2" onSubmit={(e) => {
              e.preventDefault();
              const v = new FormData(e.currentTarget).get('number');
              if (typeof v === 'string' && v.trim()) navigate('/calls/pop?number=' + encodeURIComponent(v.trim()), { replace: true });
            }}>
              <input name="number" aria-label="Caller number" placeholder="7135550100"
                className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm text-[hsl(var(--content))] w-44" />
              <button type="submit" className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors inline-flex items-center gap-1.5">
                <Search className="h-4 w-4" /> Look up
              </button>
            </form>
          }
        />
      )}

      {number && loading && (
        <div className="space-y-4" role="status" aria-label="Looking up caller">
          <CardSkeleton lines={3} />
          <CardSkeleton lines={2} />
        </div>
      )}

      {number && error && (
        <ErrorState message={error.includes('401') ? 'Your session expired — log in again to see caller details.' : error + ' — try refreshing.'} onRetry={refetch} />
      )}

      {number && data && <PopResult data={data} number={number} navigate={navigate} refetch={refetch} />}
    </div>
  );
}

function PopResult({ data, number, navigate, refetch }: {
  data: PopLookup; number: string; navigate: (to: string) => void; refetch: () => void;
}) {
  const { match, caller, company, openTickets, recentCalls, candidates } = data;
  const [selectedContact, setSelectedContact] = React.useState<{ contactId: number; companyId: number | null; name: string; company: string | null } | null>(null);
  const [actionLoading, setActionLoading] = React.useState(false);
  const [actionResult, setActionResult] = React.useState<{ ok: boolean; message: string; cwTicketId?: number } | null>(null);
  const [newTicketSummary, setNewTicketSummary] = React.useState('');
  const [newTicketDescription, setNewTicketDescription] = React.useState('');
  const [linkTicketId, setLinkTicketId] = React.useState('');
  const [showNewTicket, setShowNewTicket] = React.useState(false);
  const [showLinkTicket, setShowLinkTicket] = React.useState(false);
  const [contactTickets, setContactTickets] = React.useState<Array<{ cwTicketId: number; ticketId: string | null; summary: string; status: string | null; boardName: string | null; priority: string | null }>>([]);
  const [selectedTicketId, setSelectedTicketId] = React.useState<string>("");

  // Fetch open tickets for the selected contact
  React.useEffect(() => {
    if (selectedContact?.contactId) {
      fetch("/api/v1/calls/lookup/contact-tickets?contactId=" + selectedContact.contactId)
        .then((r) => r.json())
        .then((d) => { if (d.items) setContactTickets(d.items); })
        .catch(() => setContactTickets([]));
    } else {
      setContactTickets([]);
    }
  }, [selectedContact]);

  // Auto-select if single contact match
  React.useEffect(() => {
    if (match.outcome === 'contact' && match.contactId && !selectedContact) {
      setSelectedContact({
        contactId: match.contactId,
        companyId: match.companyId,
        name: caller?.contactName ?? 'Contact #' + match.contactId,
        company: caller?.companyName ?? null,
      });
    }
  }, [match, caller, selectedContact]);

  async function acceptCandidate(c: PopLookup['candidates'][0]) {
    setActionLoading(true);
    try {
      await apiPost('/api/v1/calls/lookup/select', {
        number, contactId: c.contactId, companyId: c.companyId,
      });
      setSelectedContact({
        contactId: c.contactId!,
        companyId: c.companyId ?? null,
        name: c.name ?? 'Contact #' + c.contactId,
        company: c.companyName ?? null,
      });
      setActionResult(null);
      refetch();
    } catch (e) {
      setActionResult({ ok: false, message: e instanceof Error ? e.message : 'Failed to select contact' });
    } finally {
      setActionLoading(false);
    }
  }

  async function createNewTicket() {
    if (!newTicketSummary.trim()) return;
    setActionLoading(true);
    try {
      const res = await apiPost<{ ok: boolean; cwTicketId: number }>('/api/v1/calls/lookup/new-ticket', {
        number, summary: newTicketSummary, description: newTicketDescription || undefined,
        contactId: selectedContact?.contactId,
        companyId: selectedContact?.companyId ?? undefined,
      });
      setActionResult({ ok: true, message: 'Ticket #' + res.cwTicketId + ' created with call details', cwTicketId: res.cwTicketId });
      setShowNewTicket(false);
    } catch (e) {
      setActionResult({ ok: false, message: e instanceof Error ? e.message : 'Failed to create ticket' });
    } finally {
      setActionLoading(false);
    }
  }

  async function linkExistingTicket() {
    const ticketId = Number(linkTicketId);
    if (!ticketId) return;
    setActionLoading(true);
    try {
      const res = await apiPost<{ ok: boolean; cwTicketId: number }>('/api/v1/calls/lookup/link-ticket', {
        number, cwTicketId: ticketId,
        contactId: selectedContact?.contactId,
      });
      setActionResult({ ok: true, message: 'Call linked to ticket #' + res.cwTicketId, cwTicketId: res.cwTicketId });
      setShowLinkTicket(false);
    } catch (e) {
      setActionResult({ ok: false, message: e instanceof Error ? e.message : 'Failed to link ticket' });
    } finally {
      setActionLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Strong match: single identity + single open ticket → deep-link CTA */}
      {match.strong && match.ticketId && !selectedContact && (
        <button type="button" data-testid="pop-strong-cta"
          onClick={() => navigate('/tickets/' + match.ticketId)}
          className="w-full rounded-xl border border-brand-600/40 bg-brand-600/10 p-4 text-left hover:bg-brand-600/15 transition-colors">
          <div className="flex items-center gap-3">
            <TicketIcon className="h-5 w-5 text-brand-600 shrink-0" aria-hidden="true" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-[hsl(var(--content))]">Open ticket #{match.ticketCwId}</p>
              <p className="text-xs text-[hsl(var(--content-subtle))] truncate">{openTickets[0]?.summary ?? 'Matched open ticket'}</p>
            </div>
            <ExternalLink className="h-4 w-4 text-brand-600 shrink-0" aria-hidden="true" />
          </div>
        </button>
      )}

      {/* Ambiguous: show candidates with name + company → accept one */}
      {match.outcome === 'ambiguous' && !selectedContact && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="pop-ambiguous">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" aria-hidden="true" />
            <p className="text-sm font-semibold text-[hsl(var(--content))]">Multiple possible matches</p>
          </div>
          <p className="text-xs text-[hsl(var(--content-subtle))] mb-3">{match.reason} — select the correct contact.</p>
          <ul className="space-y-2">
            {candidates.map((c, i) => (
              <li key={i}>
                <button type="button" disabled={actionLoading}
                  onClick={() => acceptCandidate(c)}
                  className="w-full flex items-center gap-3 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2.5 text-left text-sm hover:bg-amber-500/10 transition-colors disabled:opacity-50">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-amber-500/20 text-amber-600 shrink-0">
                    <User className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-[hsl(var(--content))]">
                      {c.name ?? 'Contact #' + c.contactId}
                    </p>
                    {c.companyName && (
                      <p className="text-xs text-[hsl(var(--content-subtle))] flex items-center gap-1">
                        <Building2 className="h-3 w-3" /> {c.companyName}
                      </p>
                    )}
                  </div>
                  <Check className="h-4 w-4 text-amber-600 shrink-0" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* No match */}
      {match.outcome === 'none' && !selectedContact && (
        <EmptyState
          icon="🔍"
          title="Caller not recognized"
          description="No contact matches this number. You can still create a ticket manually below."
          action={
            <button type="button" onClick={() => {
              setSelectedContact({ contactId: 0, companyId: null, name: 'Unknown caller', company: null });
            }}
              className="rounded-lg bg-brand-600 px-3 py-2 text-sm font-medium text-white hover:bg-brand-700 transition-colors inline-flex items-center gap-1.5">
              <Plus className="h-4 w-4" /> Create ticket for unknown caller
            </button>
          }
        />
      )}

      {/* Contact confirmed — show actions */}
      {selectedContact && (
        <div className="space-y-4">
          {/* Confirmed contact card */}
          <div className="rounded-xl border border-brand-600/40 bg-brand-600/5 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-600/20 text-brand-600 shrink-0">
                <Check className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[hsl(var(--content))]">{selectedContact.name}</p>
                {selectedContact.company && (
                  <p className="text-xs text-[hsl(var(--content-subtle))] flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {selectedContact.company}
                  </p>
                )}
                <p className="text-xs text-[hsl(var(--content-subtle))]">📞 {number}</p>
              </div>
            </div>
          </div>

          {/* Open tickets for this contact */}
          {openTickets.length > 0 && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4">
              <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">Open Tickets ({openTickets.length})</h3>
              <ul className="space-y-2">
                {openTickets.slice(0, 5).map((t) => (
                  <li key={t.id}>
                    <button type="button" onClick={() => navigate('/tickets/' + t.id)}
                      className="w-full flex items-center gap-3 rounded-lg border border-[hsl(var(--border))] px-3 py-2 text-left text-sm hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                      <TicketIcon className="h-4 w-4 text-[hsl(var(--content-subtle))] shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-[hsl(var(--content))] truncate">#{t.cwTicketId} — {t.summary ?? 'No summary'}</p>
                        <p className="text-xs text-[hsl(var(--content-subtle))]">{t.boardName ?? '—'} · {t.status ?? '—'}</p>
                      </div>
                      <ExternalLink className="h-3 w-3 text-[hsl(var(--content-subtle))] shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Action result */}
          {actionResult && (
            <div className={"rounded-xl border p-4 " + (actionResult.ok ? "border-green-500/40 bg-green-500/10" : "border-red-500/40 bg-red-500/10")}>
              <p className={"text-sm font-medium " + (actionResult.ok ? "text-green-700 dark:text-green-300" : "text-red-700 dark:text-red-300")}>
                {actionResult.ok ? '✓ ' : '✗ '}{actionResult.message}
              </p>
              {actionResult.ok && actionResult.cwTicketId && (
                <button type="button" onClick={() => navigate("/tickets/" + (contactTickets.find((t) => String(t.cwTicketId) === String(actionResult.cwTicketId))?.ticketId ?? String(actionResult.cwTicketId)))}
                  className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700">
                  <ExternalLink className="h-3 w-3" /> View ticket #{actionResult.cwTicketId}
                </button>
              )}
            </div>
          )}

          {/* Create new ticket form */}
          {showNewTicket && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4 space-y-3">
              <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Create New Ticket</h3>
              <input value={newTicketSummary} onChange={(e) => setNewTicketSummary(e.target.value)}
                placeholder="Ticket summary (e.g. 'Printer not working')"
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              <textarea value={newTicketDescription} onChange={(e) => setNewTicketDescription(e.target.value)}
                placeholder="Ticket description (detailed problem description)"
                rows={3}
                className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />

              <div className="flex gap-2">
                <button type="button" onClick={createNewTicket} disabled={actionLoading || !newTicketSummary.trim()}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-50">
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create Ticket
                </button>
                <button type="button" onClick={() => setShowNewTicket(false)}
                  className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Link existing ticket form with dropdown */}
          {showLinkTicket && (
            <div className="rounded-xl border border-[hsl(var(--border))] p-4 space-y-3">
              <h3 className="text-sm font-semibold text-[hsl(var(--content))]">Link Existing Ticket</h3>
              {contactTickets.length > 0 ? (
                <div>
                  <label className="text-xs text-[hsl(var(--content-subtle))]">Select an open ticket for {selectedContact?.name}</label>
                  <select value={selectedTicketId} onChange={(e) => { setSelectedTicketId(e.target.value); setLinkTicketId(e.target.value); }}
                    className="w-full mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500">
                    <option value="">Select ticket...</option>
                    {contactTickets.map((t) => (
                      <option key={t.cwTicketId} value={t.cwTicketId}>
                        #{t.cwTicketId} — {t.summary} ({t.status ?? "open"} · {t.boardName ?? "—"})
                      </option>
                    ))}
                  </select>
                  {selectedTicketId && (
                    <div className="mt-2 rounded-lg bg-[hsl(var(--surface-subtle))] p-3 text-sm">
                      <p className="font-medium text-[hsl(var(--content))]">#{contactTickets.find((t) => String(t.cwTicketId) === selectedTicketId)?.cwTicketId}</p>
                      <p className="text-[hsl(var(--content-subtle))]">{contactTickets.find((t) => String(t.cwTicketId) === selectedTicketId)?.summary}</p>
                      <p className="text-xs text-[hsl(var(--content-subtle))] mt-1">{contactTickets.find((t) => String(t.cwTicketId) === selectedTicketId)?.boardName} · {contactTickets.find((t) => String(t.cwTicketId) === selectedTicketId)?.priority ?? "—"}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-[hsl(var(--content-subtle))]">No open tickets found for this contact. Enter a CW ticket ID manually:</p>
              )}
              {contactTickets.length === 0 && (
                <input type="number" value={linkTicketId} onChange={(e) => setLinkTicketId(e.target.value)}
                  placeholder="CW Ticket ID (e.g. 583382)"
                  className="w-full rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              )}

              <div className="flex gap-2">
                <button type="button" onClick={linkExistingTicket} disabled={actionLoading || !linkTicketId}
                  className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 transition-colors disabled:opacity-50">
                  {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LinkIcon className="h-4 w-4" />} Link Ticket
                </button>
                <button type="button" onClick={() => { setShowLinkTicket(false); setSelectedTicketId(""); }}
                  className="rounded-lg border border-[hsl(var(--border))] px-4 py-2 text-sm text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Action buttons */}
          {!showNewTicket && !showLinkTicket && (
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowNewTicket(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 transition-colors">
                <Plus className="h-4 w-4" /> Create New Ticket
              </button>
              <button type="button" onClick={() => setShowLinkTicket(true)}
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-[hsl(var(--border))] px-4 py-2.5 text-sm font-semibold text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                <LinkIcon className="h-4 w-4" /> Link Existing
              </button>
            </div>
          )}

          {/* Reset button */}
          {(selectedContact || actionResult) && (
            <button type="button" onClick={() => { setSelectedContact(null); setActionResult(null); }}
              className="text-xs text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))] transition-colors">
              ← Back to search results
            </button>
          )}
        </div>
      )}

      {/* Recent calls */}
      {!selectedContact && recentCalls.length > 0 && (
        <div className="rounded-xl border border-[hsl(var(--border))] p-4">
          <h3 className="text-sm font-semibold text-[hsl(var(--content))] mb-3">Recent Calls</h3>
          <ul className="space-y-2">
            {recentCalls.slice(0, 5).map((c) => (
              <li key={c.id} className="flex items-center gap-3 text-sm">
                <Clock className="h-3 w-3 text-[hsl(var(--content-subtle))] shrink-0" />
                <span className="text-[hsl(var(--content-subtle))]">{new Date(c.startedAt).toLocaleString()}</span>
                <span className="text-[hsl(var(--content))]">{c.direction}</span>
                {c.durationSec && <span className="text-[hsl(var(--content-subtle))]">{Math.floor(c.durationSec / 60)}m</span>}
                {c.cwTicketId && <span className="text-brand-600">#{c.cwTicketId}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
